import React from 'react';
import { Box, Typography, Container, List, ListItem, ListItemText } from '@mui/material';

function AnalyticsDisclosure() {
    return (
        <Container maxWidth="md">
            <Box sx={{ padding: '20px' }}>
                <Typography variant="h4" gutterBottom>
                    FM Digital Official Analytics Disclosure
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                    Effective Date: (August 2024)
                </Typography>
                <Typography variant="body1" gutterBottom>
                    At FM Digital Official, we prioritize transparency and user privacy. This Analytics Disclosure outlines how we collect, use, and manage data through analytics tools to improve our services and website functionality.
                </Typography>

                <Typography variant="h6" gutterBottom>
                    1. Purpose of Analytics Collection
                </Typography>
                <Typography variant="body1" gutterBottom>
                    The purpose of collecting analytics data is to:
                </Typography>
                <List>
                    <ListItem>
                        <ListItemText primary="Understand how users interact with our website and services." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Improve the user experience by optimizing site performance and features." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Monitor and enhance the effectiveness of marketing and communication strategies." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Make data-driven decisions to improve our services and offerings." />
                    </ListItem>
                </List>

                <Typography variant="h6" gutterBottom>
                    2. Types of Data Collected
                </Typography>
                <Typography variant="body1" gutterBottom>
                    FM Digital Official uses analytics tools to collect non-personal and anonymized data. This may include:
                </Typography>
                <List>
                    <ListItem>
                        <ListItemText primary="Device Information: Type of device, operating system, and browser used to access our website." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Usage Data: Pages visited, time spent on the website, clicks, scroll behavior, and navigation patterns." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Geolocation Information: General geographical location based on IP address." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Referral Information: How users arrived at our website, such as through search engines, advertisements, or external links." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Event Tracking: Actions taken on the website, such as form submissions, downloads, or video plays." />
                    </ListItem>
                </List>

                <Typography variant="h6" gutterBottom>
                    3. Use of Analytics Data
                </Typography>
                <Typography variant="body1" gutterBottom>
                    The collected analytics data is used solely for the following purposes:
                </Typography>
                <List>
                    <ListItem>
                        <ListItemText primary="Website and Service Optimization: We analyze usage patterns to improve website navigation, content, and overall user experience." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Marketing Performance: Tracking the effectiveness of online marketing campaigns and advertisements." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="User Insights: Understanding user preferences and behavior to provide better services, support, and content recommendations." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Security and Fraud Detection: Detecting and preventing fraudulent activities or security issues." />
                    </ListItem>
                </List>

                <Typography variant="h6" gutterBottom>
                    4. Analytics Tools
                </Typography>
                <Typography variant="body1" gutterBottom>
                    We may use third-party analytics tools, such as:
                </Typography>
                <List>
                    <ListItem>
                        <ListItemText primary="Google Analytics: A tool provided by Google that tracks and reports website traffic. For more information, please see Google’s Privacy Policy." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Facebook Analytics: Used to measure and track interactions originating from social media platforms." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Other Third-Party Tools: We may integrate other third-party services as needed to improve our analytics capabilities." />
                    </ListItem>
                </List>

                <Typography variant="h6" gutterBottom>
                    5. Data Sharing and Security
                </Typography>
                <Typography variant="body1" gutterBottom>
                    Analytics data is collected and processed in accordance with FM Digital Official’s Privacy Policy. The information is used for internal purposes only and is shared with third-party analytics providers under strict data protection agreements. We take reasonable steps to ensure that any shared data is anonymized and secure.
                </Typography>

                <Typography variant="h6" gutterBottom>
                    6. Your Control Over Data Collection
                </Typography>
                <Typography variant="body1" gutterBottom>
                    Users have the option to control and limit the data collected through analytics tools:
                </Typography>
                <List>
                    <ListItem>
                        <ListItemText primary="Opt-Out from Google Analytics: You can opt out of Google Analytics by installing the Google Analytics opt-out browser add-on." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Cookies Control: Users can manage cookie preferences through their browser settings to control how data is collected and shared." />
                    </ListItem>
                </List>

                <Typography variant="h6" gutterBottom>
                    7. Cookies and Tracking Technologies
                </Typography>
                <Typography variant="body1" gutterBottom>
                    FM Digital Official uses cookies and other tracking technologies (such as web beacons or pixel tags) to enhance the accuracy of the analytics data. Cookies help us recognize returning users and their preferences. For more detailed information on cookies, please refer to our Cookie Policy.
                </Typography>

                <Typography variant="h6" gutterBottom>
                    8. Data Retention
                </Typography>
                <Typography variant="body1" gutterBottom>
                    We retain analytics data for as long as necessary to fulfill the purposes described in this disclosure and our Privacy Policy. Data may be retained for longer periods when required by law or for legitimate business needs.
                </Typography>

                <Typography variant="h6" gutterBottom>
                    9. Review of Analytics Disclosure
                </Typography>
                <Typography variant="body1" gutterBottom>
                    This Analytics Disclosure may be updated periodically to reflect any changes in our data collection and usage practices. Users are encouraged to review this document regularly to stay informed of any updates.
                </Typography>

                <Typography variant="h6" gutterBottom>
                    10. Contact Information
                </Typography>
                <Typography variant="body1">
                    If you have any questions regarding this Analytics Disclosure or wish to opt out of specific data collection methods, please contact:
                </Typography>
                <Typography variant="body1">
                    FM Digital Official <br />
                    India, Asia
                </Typography>
            </Box>
        </Container>
    );
}

export default AnalyticsDisclosure;
